import { useId, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowDownIcon, ArrowUpIcon, seriesPoints, smoothPath, type Point } from "@signet/ui";

/* ──────────────────────────────────────────────────────────────────────────
   One figure in the row across the top of Now.

   The boss's mockup (2026-09-29): a tinted icon, the label, the figure, the
   change against the period before in green or red with an arrow, and the
   trend as a soft filled line along the bottom. Built here rather than on
   @signet/ui's Stat because the layout is different, not the maths: the line
   comes from the same chart helpers.

   The arrow is there so the change never rests on colour alone.
   ────────────────────────────────────────────────────────────────────────── */

export type NowStatProps = {
  icon: ReactNode;
  label: string;
  /** A CSS colour, usually a token: the icon's tint and the line. */
  colour: string;
  /** Formatted. Null while loading. */
  value: string | null;
  /** The value is words ("No figures yet"), not a figure, and is drawn quieter. */
  empty?: boolean;
  /** The raw numbers behind the change. No change is shown when there is nothing to compare to. */
  current?: number | null;
  previous?: number | null;
  versus?: string;
  spark?: number[];
  /** A small warning beside the label, such as figures that are over a day old. */
  chip?: string | null;
  /** Quieter words after the label, such as when the day's figures were submitted. */
  note?: string | null;
  /** A quiet line under the figure, such as which day it is for. */
  caption?: string | null;
  href?: string;
  /** The address's query for href: the page it opens filtered to the card's period (Jay, 2026-10-13). */
  search?: Record<string, string>;
  failed?: boolean;
  children?: ReactNode;
  /** Under the line, across the card's foot: Sub flow's top three (2026-10-13). */
  footer?: ReactNode;
  className?: string;
};

const SPARK = { width: 240, height: 44 };

export function NowStat({ icon, label, colour, value, empty, current, previous, versus, spark, chip, note, caption, href, search, failed, children, footer, className }: NowStatProps) {
  const gradient = useId();
  const change =
    current === null || current === undefined || previous === null || previous === undefined || previous === 0
      ? null
      : ((current - previous) / previous) * 100;

  // Scaled from zero, as the charts these lines come from are (Reporting's
  // earnings chart, the Links overview). Scaling to the line's own low point
  // made a quiet revenue day plunge to the floor, and Jay saw the Now line
  // disagree with Reporting's Month chart (2026-09-30). The padding is
  // Reporting's: 14% above the peak, 5% below the lowest point, which keeps a
  // negative day (a refund) below the axis where Reporting draws it.
  const high = Math.max(0, ...(spark ?? []));
  const low = Math.min(0, ...(spark ?? []));
  const span = high - low || 1;
  const top = high + span * 0.14;
  const bottom = low - span * 0.05;
  const points =
    spark && spark.length > 1
      ? (seriesPoints({ values: spark.map((v) => v - bottom), max: top - bottom, ...SPARK, padding: 3 }).filter(Boolean) as Point[])
      : [];
  const line = smoothPath(points);
  const area = points.length ? `${line} L ${points.at(-1)!.x} ${SPARK.height} L ${points[0]!.x} ${SPARK.height} Z` : "";

  const body = (
    <>
      <div className="signet-stat-top">
        <span className="signet-stat-icon" aria-hidden="true">{icon}</span>
        <div className="signet-stat-text">
          <span className="signet-stat-label">
            {label}
            {note && !failed ? <span className="signet-stat-submitted">{note}</span> : null}
            {chip ? <span className="signet-stat-chip">{chip}</span> : null}
          </span>
          {failed ? (
            <span className="signet-stat-failed">Couldn't load this</span>
          ) : value === null ? (
            <span className="signet-stat-value signet-stat-loading" aria-label="Loading" />
          ) : (
            <span className={`signet-stat-value${empty ? " empty" : ""}`}>{value}</span>
          )}
          {change !== null && !failed ? (
            <span className={`signet-stat-change ${change >= 0 ? "up" : "down"}`}>
              {change >= 0 ? <ArrowUpIcon size={11} /> : <ArrowDownIcon size={11} />}
              <strong>{Math.abs(change).toFixed(1)}%</strong>
              {versus ? <span>{versus}</span> : null}
            </span>
          ) : null}
          {caption && !failed ? <span className="signet-stat-caption">{caption}</span> : null}
        </div>
      </div>
      {children}
      {line && !failed ? (
        <svg className="signet-stat-spark" viewBox={`0 0 ${SPARK.width} ${SPARK.height}`} preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={colour} stopOpacity=".32" />
              <stop offset="100%" stopColor={colour} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gradient})`} />
          <path d={line} fill="none" stroke={colour} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
      ) : null}
      {footer && !failed ? footer : null}
    </>
  );

  const style = { "--signet-stat-colour": colour } as React.CSSProperties;
  return href ? (
    <Link to={href} search={search as never} className={`signet-stat${className ? ` ${className}` : ""}`} style={style}>{body}</Link>
  ) : (
    <div className={`signet-stat${className ? ` ${className}` : ""}`} style={style}>{body}</div>
  );
}

const shortDay = (day: string) => new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });

/** The words after a card's label: "today", "last 7 days", "3 Sep to 9 Sep". */
export function nowRangeWords(range: { key: string; from?: string; to?: string }): string {
  if (range.key === "yesterday") return "yesterday";
  if (range.key === "7d") return "last 7 days";
  if (range.key === "30d") return "last 30 days";
  if (range.key === "custom" && range.from && range.to) return range.from === range.to ? shortDay(range.from) : `${shortDay(range.from)} to ${shortDay(range.to)}`;
  return "today";
}

/** A day as the cards write it: "28 Sep". */
export const nowDay = shortDay;

/**
 * "Submitted 1:42pm Today" in the viewer's own clock, for a one-day card (Jay, 2026-10-15). Always
 * 12-hour and always with the day: the locale's own format showed the boss "Submitted 13:17", and a
 * bare time let a morning export for yesterday read as this morning's (Jay, 2026-10-19).
 */
export function submittedWords(at: string | null | undefined, now = new Date()): string | null {
  const when = at ? new Date(at) : null;
  if (!when || Number.isNaN(when.getTime())) return null;
  const hour = when.getHours() % 12 || 12;
  const time = `${hour}:${String(when.getMinutes()).padStart(2, "0")}${when.getHours() < 12 ? "am" : "pm"}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const day =
    when.toDateString() === now.toDateString() ? "Today"
    : when.toDateString() === yesterday.toDateString() ? "Yesterday"
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][when.getDay()];
  return `Submitted ${time} ${day}`;
}

